import React from "react";
import { Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import { formatLeagueDateTime } from "utils/league-datetime";
import { pushStatusColor, pushStatusLabelId } from "components/pages/Gestion/push-broadcast-helpers";

/**
 * History of sent broadcasts, in API order (newest first). Purely presentational.
 * Props: { rows: Array<{id,title,body,status,created_at,sender}>, loading: boolean, onRefresh?: () => void }
 */
const PushBroadcastHistory = ({ rows = [], loading = false, onRefresh }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <RevaText variant="h6">{t("gestion.push.history.title")}</RevaText>
        {onRefresh ? (
          <Button size="small" data-testid="push-history-refresh" onClick={onRefresh} disabled={loading}>
            {t("gestion.push.history.refresh")}
          </Button>
        ) : null}
      </Box>

      {loading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && rows.length === 0 ? (
        <Box
          data-testid="push-history-empty"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 4,
            color: "text.secondary",
          }}
        >
          <NotificationsNoneOutlinedIcon />
          <RevaText variant="body2" color="text.secondary">
            {t("gestion.push.history.empty")}
          </RevaText>
        </Box>
      ) : null}

      {rows.length > 0 ? (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("gestion.push.history.col.date")}</TableCell>
                <TableCell>{t("gestion.push.history.col.message")}</TableCell>
                <TableCell>{t("gestion.push.history.col.sender")}</TableCell>
                <TableCell>{t("gestion.push.history.col.status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid="push-history-row">
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatLeagueDateTime(row.created_at)}</TableCell>
                  <TableCell>
                    <RevaText variant="body2" sx={{ fontWeight: 700 }}>
                      {row.title}
                    </RevaText>
                    <RevaText variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                      {row.body}
                    </RevaText>
                  </TableCell>
                  <TableCell>{row.sender ? row.sender.name || "—" : t("gestion.push.history.deletedSender")}</TableCell>
                  <TableCell>
                    <Chip size="small" label={t(pushStatusLabelId(row.status))} color={pushStatusColor(row.status)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Box>
  );
};

export default PushBroadcastHistory;
